import { DeviceResource } from '@jarvis/react-device-resources-provider'
import { Logger } from '../../utils/helperMethods'
import { BootstrapResourceDevice } from './BootstrapResourceDevice'
import { LoaderRecipe } from './LoaderRecipe'
import {
  RecipeBootstrap,
  RecipeCDM,
  RecipeLEDM,
  RecipePreFetch,
  RecipeProductNumber
} from './RecipeFileVersion'
import { ErrorCode } from '../bootstrap-events'
import { DeviceResourceRecipeType } from './DeviceResourceRecipeType'
import { ResourceError } from '../../interface/controllerInterface'

export const getDeviceResources = async (
  sessionId: string,
  recipeType: DeviceResourceRecipeType,
  isDebugMode: boolean
): Promise<DeviceResource[] | ResourceError> => {
  try {
    return await DeviceResourceRecipe.getDeviceResources(
      sessionId,
      recipeType,
      isDebugMode
    )
  } catch (error) {
    Logger.error(
      `DeviceResourceRecipe.getDeviceResources failed with :${error} `
    )
    return { errorCode: ErrorCode.EBS00P0015 }
  }
}

class DeviceResourceRecipe {
  /**
   * Get the resources for the device, using the oobe-bootstrap-controller-recipe
   * @param {string} sessionId The session identifier
   * @returns {Promise<DeviceResource[] | ResourceError>} A list of `DeviceResource`s or an `ResourceError` in case of error
   */
  public static getDeviceResources = async (
    sessionId: string,
    recipeType: DeviceResourceRecipeType,
    isDebugMode: boolean
  ): Promise<DeviceResource[] | ResourceError> => {
    Logger.log(
      `resourceProvider.getResource started for sessionId:${sessionId} `
    )

    let predicateName: string
    let version: string
    switch (recipeType) {
      case DeviceResourceRecipeType.Bootstrap:
        predicateName = RecipeBootstrap.predicate
        version = RecipeBootstrap.version
        break
      case DeviceResourceRecipeType.BootstrapPostcard:
        predicateName = RecipeBootstrap.predicate
        version = RecipeBootstrap.versionPostcard
        break
      case DeviceResourceRecipeType.BootstrapCDMOnlyPostcard:
        predicateName = RecipeBootstrap.predicate
        version = RecipeBootstrap.versionCDMOnlyPostcard
        break
      case DeviceResourceRecipeType.Prefetch:
        predicateName = RecipePreFetch.predicate
        version = RecipePreFetch.version
        break
      case DeviceResourceRecipeType.CDM:
        predicateName = RecipeCDM.predicate
        version = RecipeCDM.version
        break
      case DeviceResourceRecipeType.LEDM:
        predicateName = RecipeLEDM.predicate
        version = RecipeLEDM.version
        break
      case DeviceResourceRecipeType.ProductNumber:
        predicateName = RecipeProductNumber.predicate
        version = RecipeProductNumber.version
        break
      default:
        throw new Error(`Invalid resource type: ${recipeType}`)
    }

    const loaderRecipe: LoaderRecipe = new LoaderRecipe()
    const jsonRecipe = loaderRecipe.readRecipeFile(predicateName, version)

    const bootstrapResourceDevice: BootstrapResourceDevice = new BootstrapResourceDevice()
    const result = await bootstrapResourceDevice.getResourceFromRecipe(
      sessionId,
      predicateName,
      JSON.stringify(jsonRecipe, null, 0),
      isDebugMode
    )

    Logger.log(
      `resourceProvider.getResource finished request for recipe: ${predicateName}${version} using sessionId: ${sessionId}`
    )

    return result
  }
}
